/**
 * Data-access bootstrap for the console.
 *
 * Registers the concrete Firebase Web SDK implementations of the data sources,
 * auth, file storage and the Postgres REST factory, then registers the domain
 * entity descriptors + module manifests. Must run before any repository,
 * auth or registry operation.
 */

import {
  authFactory,
  createModularAuthAdapter,
  createModularFirestoreAdapter,
  createModularStorageAdapter,
  createWebLocalStorageAdapter,
  dataSourceRegistry,
  fileStorageRegistry,
  FileStorageStrategy,
  FirebaseStorageProvider,
  FirestoreDataSource,
  integrationFactory,
  LocalStorageDataSource,
  PostgresRestDataSource,
  repositoryResolver,
} from '@rammetec/data-access'
import { registerDescriptors, registerManifests, registerOrgParamGroups, registerSyncConnectors } from '@syscolect/domain'
import { globalConfigService } from '@syscolect/application'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth'
import { initializeApp, deleteApp } from 'firebase/app'
import { env } from '@/config/env'
import { firebaseApp, firebaseConfig, firestoreDb, firebaseStorage } from './firebase'
import { buildRestScriptOptions } from './restScriptOptions'

let bootstrapped = false

export function bootstrapDataAccess(): void {
  if (bootstrapped) return
  bootstrapped = true

  // Firestore
  const firestoreAdapter = createModularFirestoreAdapter({
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
  })
  dataSourceRegistry.registerFirestore((path, tableName) => {
    return new FirestoreDataSource(path, tableName, firestoreDb, firestoreAdapter)
  })

  // LocalStorage
  const localStorageAdapter = createWebLocalStorageAdapter()
  dataSourceRegistry.registerLocalStorage((path, tableName) => {
    return new LocalStorageDataSource(path, tableName, localStorageAdapter)
  })

  // Auth (Firebase Web SDK, modular)
  const authAdapter = createModularAuthAdapter({
    getAuth: () => getAuth(firebaseApp),
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    onAuthStateChanged,
    persistence: {
      setPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    },
    secondaryApp: {
      initializeApp,
      deleteApp,
      getAuth,
      config: firebaseConfig,
    },
  })
  authFactory.configure({ adapter: authAdapter, firebaseConfig })

  // Firebase Storage
  const storageAdapter = createModularStorageAdapter(firebaseStorage, {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject,
  })
  fileStorageRegistry.register(
    FileStorageStrategy.Firebase,
    new FirebaseStorageProvider(storageAdapter),
  )

  // Session token provider for integrations that use the current user's auth
  integrationFactory.registerAuthTokenProvider('session-token', async () => {
    const authService = authFactory.getAuthService()
    return (await authService.getIdToken()) ?? null
  })

  // Postgres REST — routes through system-api's /pg/v1 endpoints. The factory is
  // always registered; runtime options are only provided when the host is set.
  dataSourceRegistry.registerPostgresRest((path, tableName, options) => {
    if (!options?.postgresRest) {
      throw new Error(
        '[data-access.bootstrap] PostgresRest strategy requires options.postgresRest ' +
          '(check VITE_SYSTEM_API_HOST and registered resolver options).',
      )
    }
    return new PostgresRestDataSource(path, tableName, options.postgresRest)
  })

  // Postgres REST host: env-first, globalConfig fallback (same pattern as the WS /
  // monitor hosts). Resolved lazily at repo-resolution time, so the value set in the
  // Global Config module (loaded by <ConfigGate> at boot) is honored without an env var.
  // Kept on its own resolution path so it can be repointed to a dedicated server later.
  repositoryResolver.registerPostgresRestOptions(() => {
    const host = env.systemApiHost ?? globalConfigService.getConfig()?.systemApiHost
    if (!host) {
      throw new Error(
        '[data-access.bootstrap] PostgresRest host não configurado ' +
          '(defina VITE_SYSTEM_API_HOST ou systemApiHost no Global Config).',
      )
    }
    return {
      baseUrl: host,
      getToken: async () => {
        const authService = authFactory.getAuthService()
        return (await authService.getIdToken()) ?? null
      },
    }
  })

  // RestScript — legacy entity reads via VectorHub agent (`data` module).
  // Host is resolved lazily (env-first + Global Config fallback), same as PostgresRest.
  // Strategy is selected per org in OrgEntityConfigDialog; one provider serves all RestScript entities.
  // Options factory shared with product-import (and any other one-off RestScript open).
  repositoryResolver.registerRestScriptOptions(() => buildRestScriptOptions())

  // Domain registration: descriptors before any repository is built; manifests
  // and org param groups any time before their respective UIs render — bootstrap
  // is simplest.
  registerDescriptors()
  registerManifests()
  registerOrgParamGroups()
  registerSyncConnectors()
}
